import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { QRPreview } from "@/components/QRPreview";
import { QR_TYPES, DEFAULT_DESIGN, DOT_TYPES, CORNER_SQUARE_TYPES, CORNER_DOT_TYPES, computePreviewData } from "@/lib/qrTypes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { tagColor } from "@/lib/tags";
import { ArrowLeft, Upload, Download, Loader2, Check, X, FolderOpen, CalendarClock, Trash2 } from "lucide-react";
import { toast } from "sonner";

const SWATCHES = ["#0A0A0B", "#0033CC", "#00C781", "#FF3B30", "#FFB800", "#7C3AED", "#DB2777", "#0891B2"];

export default function Editor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(id);
  const qrRef = useRef(null);

  const [type, setType] = useState("url");
  const [name, setName] = useState("");
  const [payload, setPayload] = useState({});
  const [design, setDesign] = useState(DEFAULT_DESIGN);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savedQr, setSavedQr] = useState(null);
  const [folders, setFolders] = useState([]);
  const [folderId, setFolderId] = useState("none");
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [schedules, setSchedules] = useState([]);
  const [schedDate, setSchedDate] = useState("");
  const [schedUrl, setSchedUrl] = useState("");

  useEffect(() => {
    api.get("/folders").then(({ data }) => setFolders(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (editing) {
      api.get(`/qrcodes/${id}`).then(({ data }) => {
        setType(data.type);
        setName(data.name);
        setPayload(data.payload || {});
        setDesign({ ...DEFAULT_DESIGN, ...(data.design || {}) });
        setSavedQr(data);
        setFolderId(data.folder_id || "none");
        setTags(data.tags || []);
        setSchedules(data.schedules || []);
      }).catch(() => toast.error("QR code not found"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const addTag = (raw) => {
    const t = raw.trim().toLowerCase();
    if (t && !tags.includes(t)) setTags((prev) => [...prev, t]);
    setTagInput("");
  };

  const addSchedule = () => {
    if (!schedDate || !schedUrl.trim()) return toast.error("Add a date and a destination URL");
    const iso = new Date(schedDate).toISOString();
    const id = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`);
    setSchedules((prev) => [...prev, { id, activate_at: iso, url: schedUrl.trim() }]);
    setSchedDate("");
    setSchedUrl("");
  };

  const isLinkType = ["url", "pdf", "audio", "video", "image"].includes(type);

  const previewData = savedQr?.redirect_url || computePreviewData(type, payload);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPayload((p) => ({ ...p, url: data.url, filename: data.filename }));
      toast.success(`Uploaded ${data.filename}`);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setDesign((d) => ({ ...d, logo: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Please name your QR code");
    setSaving(true);
    try {
      const folder_id = folderId === "none" ? null : folderId;
      if (editing) {
        const { data } = await api.put(`/qrcodes/${id}`, {
          name, type, payload, design, tags, schedules,
          folder_id, clear_folder: folder_id === null,
        });
        setSavedQr(data);
        toast.success("QR code updated");
      } else {
        const { data } = await api.post("/qrcodes", { name, type, payload, design, folder_id, tags, schedules });
        setSavedQr(data);
        toast.success("QR code created");
        navigate(`/edit/${data.id}`, { replace: true });
      }
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const T = QR_TYPES[type];

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-7xl mx-auto">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </button>
        <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight mb-8">
          {editing ? "Edit QR Code" : "Create QR Code"}
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Controls */}
          <div className="lg:col-span-7 space-y-6">
            {/* Type selector — available on create AND edit (same QR, new content type) */}
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Content type</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                {Object.entries(QR_TYPES).map(([key, t]) => (
                  <button
                    key={key}
                    onClick={() => {
                      if (key !== type) {
                        setType(key);
                        setPayload({});
                        if (!["url", "pdf", "audio", "video", "image"].includes(key)) setSchedules([]);
                      }
                    }}
                    data-testid={`type-${key}`}
                    className={`flex items-center gap-2 p-3 rounded-lg border text-sm text-left transition-colors ${
                      type === key ? "border-primary bg-primary/5 text-foreground" : "border-border hover:border-primary/40 text-muted-foreground"
                    }`}
                  >
                    <t.icon className={`h-4 w-4 shrink-0 ${type === key ? "text-primary" : ""}`} />
                    <span className="truncate">{t.label}</span>
                  </button>
                ))}
              </div>
              {editing && (
                <p className="text-xs text-muted-foreground mt-3">
                  Changing the type updates this same QR code — the printed/shared code and its scan history stay intact.
                </p>
              )}
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="qr-name">Name <span className="text-muted-foreground font-normal">(internal reference)</span></Label>
              <Input id="qr-name" data-testid="qr-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Restaurant menu Q3" />
            </div>

            {/* Payload */}
            <div className="border border-border rounded-xl bg-card p-5 space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <T.icon className="h-4 w-4 text-primary" />
                <h3 className="font-display font-semibold">{T.label} content</h3>
              </div>
              <PayloadForm type={type} payload={payload} setPayload={setPayload} onFileUpload={handleFileUpload} uploading={uploading} accept={T.accept} />
            </div>

            {/* Design */}
            <Accordion type="single" collapsible defaultValue="design" className="border border-border rounded-xl bg-card px-5">
              <AccordionItem value="design" className="border-none">
                <AccordionTrigger className="font-display font-semibold hover:no-underline">Design & customization</AccordionTrigger>
                <AccordionContent className="space-y-6 pb-5">
                  <ColorField label="Foreground color" value={design.fgColor} onChange={(v) => setDesign((d) => ({ ...d, fgColor: v }))} testid="fg-color" />
                  <ColorField label="Background color" value={design.bgColor} onChange={(v) => setDesign((d) => ({ ...d, bgColor: v }))} testid="bg-color" />
                  <StyleSelect label="Dot style" value={design.dotType} options={DOT_TYPES} onChange={(v) => setDesign((d) => ({ ...d, dotType: v }))} testid="dot-type" />
                  <StyleSelect label="Corner square" value={design.cornerSquareType} options={CORNER_SQUARE_TYPES} onChange={(v) => setDesign((d) => ({ ...d, cornerSquareType: v }))} testid="corner-square" />
                  <StyleSelect label="Corner dot" value={design.cornerDotType} options={CORNER_DOT_TYPES} onChange={(v) => setDesign((d) => ({ ...d, cornerDotType: v }))} testid="corner-dot" />
                  <div className="space-y-2">
                    <Label>Center logo</Label>
                    <div className="flex items-center gap-3">
                      <label className="inline-flex items-center gap-2 px-3 py-2 border border-border rounded-md text-sm cursor-pointer hover:bg-secondary transition-colors">
                        <Upload className="h-4 w-4" /> Upload logo
                        <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} data-testid="logo-upload" />
                      </label>
                      {design.logo && (
                        <Button variant="ghost" size="sm" onClick={() => setDesign((d) => ({ ...d, logo: null }))}>Remove</Button>
                      )}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            {/* Organization: folder + tags */}
            <div className="border border-border rounded-xl bg-card p-5 space-y-4">
              <div className="flex items-center gap-2">
                <FolderOpen className="h-4 w-4 text-primary" />
                <h3 className="font-display font-semibold">Organization</h3>
              </div>
              <div className="space-y-2">
                <Label>Folder</Label>
                <Select value={folderId} onValueChange={setFolderId}>
                  <SelectTrigger data-testid="folder-select"><SelectValue placeholder="No folder" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No folder</SelectItem>
                    {folders.map((f) => (
                      <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tags</Label>
                <Input
                  data-testid="tag-input"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(tagInput); }
                  }}
                  placeholder="Type a tag and press Enter"
                />
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1" data-testid="tag-list">
                    {tags.map((t) => {
                      const c = tagColor(t);
                      return (
                        <span key={t} className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: c.bg, color: c.fg }}>
                          {t}
                          <button onClick={() => setTags((prev) => prev.filter((x) => x !== t))} className="hover:opacity-70">
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Scheduled destination switches (link/file types only) */}
            {isLinkType && (
              <div className="border border-border rounded-xl bg-card p-5 space-y-4" data-testid="schedule-card">
                <div className="flex items-center gap-2">
                  <CalendarClock className="h-4 w-4 text-primary" />
                  <h3 className="font-display font-semibold">Scheduled destination switch</h3>
                </div>
                <p className="text-sm text-muted-foreground -mt-2">
                  Automatically point this QR to a new URL at a chosen date &amp; time — perfect for limited-time promos.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_1.4fr_auto] gap-2 items-end">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Switch at</Label>
                    <Input type="datetime-local" value={schedDate} onChange={(e) => setSchedDate(e.target.value)} data-testid="schedule-date" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">New destination URL</Label>
                    <Input value={schedUrl} onChange={(e) => setSchedUrl(e.target.value)} placeholder="https://example.com/promo" data-testid="schedule-url" />
                  </div>
                  <Button type="button" variant="outline" onClick={addSchedule} data-testid="schedule-add-btn">Add</Button>
                </div>
                {schedules.length > 0 && (
                  <div className="space-y-2 pt-1" data-testid="schedule-list">
                    {[...schedules].sort((a, b) => new Date(a.activate_at) - new Date(b.activate_at)).map((s) => {
                      const past = new Date(s.activate_at) <= new Date();
                      return (
                        <div key={s.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-border bg-background">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${past ? "bg-success/15 text-success" : "bg-warning/15 text-[#b37f00]"}`}>
                            {past ? "ACTIVE" : "PENDING"}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs text-muted-foreground">{new Date(s.activate_at).toLocaleString()}</p>
                            <p className="text-sm truncate">{s.url}</p>
                          </div>
                          <button onClick={() => setSchedules((prev) => prev.filter((x) => x.id !== s.id))} className="text-muted-foreground hover:text-destructive shrink-0" data-testid={`schedule-remove-${s.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <Button onClick={save} disabled={saving} size="lg" className="w-full" data-testid="save-qr-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
              {editing ? "Save changes" : "Create QR code"}
            </Button>
          </div>

          {/* Sticky preview */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-8">
              <div className="border border-border rounded-2xl bg-card p-8 shadow-[0_8px_32px_rgba(0,0,0,0.06)]">
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground text-center mb-6">Live preview</p>
                <div className="rounded-xl p-4 flex items-center justify-center" style={{ background: design.bgColor }}>
                  <QRPreview ref={qrRef} data={previewData} design={design} size={240} />
                </div>
                {savedQr ? (
                  <>
                    <div className="mt-6 flex gap-2">
                      <Button variant="outline" className="flex-1" onClick={() => qrRef.current?.download("png", name || "qrcode")} data-testid="download-png-btn">
                        <Download className="h-4 w-4 mr-2" /> PNG
                      </Button>
                      <Button variant="outline" className="flex-1" onClick={() => qrRef.current?.download("svg", name || "qrcode")} data-testid="download-svg-btn">
                        <Download className="h-4 w-4 mr-2" /> SVG
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground text-center mt-4 break-all">{savedQr.redirect_url}</p>
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground text-center mt-6">Save to enable downloads and scan tracking.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex items-center gap-3">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-9 w-12 rounded border border-border cursor-pointer" data-testid={testid} />
        <Input value={value} onChange={(e) => onChange(e.target.value)} className="w-28 font-mono text-sm" />
        <div className="flex gap-1.5 flex-wrap">
          {SWATCHES.map((c) => (
            <button key={c} onClick={() => onChange(c)} className="h-6 w-6 rounded-full border border-border" style={{ background: c }} />
          ))}
        </div>
      </div>
    </div>
  );
}

function StyleSelect({ label, value, options, onChange, testid }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o} value={o} className="capitalize">{o.replace(/-/g, " ")}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input {...props} />
    </div>
  );
}

function PayloadForm({ type, payload, setPayload, onFileUpload, uploading, accept }) {
  const set = (k, v) => setPayload((p) => ({ ...p, [k]: v }));

  if (["pdf", "audio", "video", "image"].includes(type)) {
    return (
      <Tabs defaultValue="upload">
        <TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger value="upload" data-testid="tab-upload">Upload file</TabsTrigger>
          <TabsTrigger value="link" data-testid="tab-link">Use a link</TabsTrigger>
        </TabsList>
        <TabsContent value="upload" className="pt-4">
          <label className="flex flex-col items-center justify-center gap-2 border border-dashed border-border rounded-lg p-8 cursor-pointer hover:border-primary/40 transition-colors">
            {uploading ? <Loader2 className="h-6 w-6 animate-spin text-primary" /> : <Upload className="h-6 w-6 text-muted-foreground" />}
            <span className="text-sm text-muted-foreground">{uploading ? "Uploading..." : "Click to upload"}</span>
            <input type="file" accept={accept} className="hidden" onChange={onFileUpload} data-testid="file-upload" />
          </label>
          {payload.filename && <p className="text-sm text-success mt-3 flex items-center gap-1.5"><Check className="h-4 w-4" /> {payload.filename}</p>}
        </TabsContent>
        <TabsContent value="link" className="pt-4">
          <Field label="File URL" data-testid="payload-url" value={payload.url || ""} onChange={(e) => set("url", e.target.value)} placeholder="https://example.com/file.pdf" />
        </TabsContent>
      </Tabs>
    );
  }

  switch (type) {
    case "url":
      return <Field label="Website URL" data-testid="payload-url" value={payload.url || ""} onChange={(e) => set("url", e.target.value)} placeholder="https://example.com" />;
    case "text":
      return (
        <div className="space-y-2">
          <Label>Text</Label>
          <Textarea data-testid="payload-text" value={payload.text || ""} onChange={(e) => set("text", e.target.value)} placeholder="Enter any text..." rows={4} />
        </div>
      );
    case "phone":
      return <Field label="Phone number" data-testid="payload-phone" value={payload.phone || ""} onChange={(e) => set("phone", e.target.value)} placeholder="+1234567890" />;
    case "sms":
      return (
        <div className="space-y-4">
          <Field label="Phone number" data-testid="payload-phone" value={payload.phone || ""} onChange={(e) => set("phone", e.target.value)} placeholder="+1234567890" />
          <div className="space-y-2"><Label>Message</Label><Textarea data-testid="payload-message" value={payload.message || ""} onChange={(e) => set("message", e.target.value)} rows={3} /></div>
        </div>
      );
    case "email":
      return (
        <div className="space-y-4">
          <Field label="To" data-testid="payload-to" value={payload.to || ""} onChange={(e) => set("to", e.target.value)} placeholder="hello@example.com" />
          <Field label="Subject" data-testid="payload-subject" value={payload.subject || ""} onChange={(e) => set("subject", e.target.value)} />
          <div className="space-y-2"><Label>Body</Label><Textarea data-testid="payload-body" value={payload.body || ""} onChange={(e) => set("body", e.target.value)} rows={3} /></div>
        </div>
      );
    case "wifi":
      return (
        <div className="space-y-4">
          <Field label="Network name (SSID)" data-testid="payload-ssid" value={payload.ssid || ""} onChange={(e) => set("ssid", e.target.value)} />
          <Field label="Password" data-testid="payload-password" value={payload.password || ""} onChange={(e) => set("password", e.target.value)} />
          <div className="space-y-2">
            <Label>Encryption</Label>
            <Select value={payload.encryption || "WPA"} onValueChange={(v) => set("encryption", v)}>
              <SelectTrigger data-testid="payload-encryption"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="WPA">WPA/WPA2</SelectItem>
                <SelectItem value="WEP">WEP</SelectItem>
                <SelectItem value="nopass">None</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );
    case "vcard":
      return (
        <div className="grid grid-cols-2 gap-4">
          <Field label="First name" data-testid="payload-firstName" value={payload.firstName || ""} onChange={(e) => set("firstName", e.target.value)} />
          <Field label="Last name" data-testid="payload-lastName" value={payload.lastName || ""} onChange={(e) => set("lastName", e.target.value)} />
          <Field label="Phone" data-testid="payload-phone" value={payload.phone || ""} onChange={(e) => set("phone", e.target.value)} />
          <Field label="Email" data-testid="payload-email" value={payload.email || ""} onChange={(e) => set("email", e.target.value)} />
          <Field label="Company" data-testid="payload-org" value={payload.org || ""} onChange={(e) => set("org", e.target.value)} />
          <Field label="Title" data-testid="payload-title" value={payload.title || ""} onChange={(e) => set("title", e.target.value)} />
          <div className="col-span-2"><Field label="Website" data-testid="payload-website" value={payload.website || ""} onChange={(e) => set("website", e.target.value)} /></div>
        </div>
      );
    default:
      return null;
  }
}
